import moment from "moment-timezone";
import type { CalendarEventType } from "../../../../types/calendar";
import type { Employee, SalonService } from "../../../../types/salon";
import type { StaffHoursOverride } from "../../../../types/scheduleAdjustment";
import {
  dayOf,
  staffServiceFields,
  staffServiceFor,
} from "../booking/bookingTypes";

/** Statuses that don't occupy the provider (prototype). */
const FREE_STATUSES = new Set(["Cancelled", "Declined", "No Show"]);

/** An appointment / time block occupying the provider today. */
export interface BusyInterval {
  start: Date;
  end: Date;
  /** "With Ben Ortiz" / "Break". */
  label: string;
}

/** One provider in the assign picker, as of `now`. */
export interface StaffAvailability {
  id: string;
  /** "Sarah Kim". */
  name: string;
  initials: string;
  /**
   * No appointment and no time block overlapping now, and now inside the
   * provider's working hours (decided 2026-10-13).
   */
  available: boolean;
  /** Inside working hours now. */
  working: boolean;
  /** Available: the next booking / block start today (null = rest of day). */
  nextAt: Date | null;
  /**
   * Not available: the "After …" start. Raw rows = the current appointment /
   * block end; rows fitted to a service (`qualifiedFor`) = the first gap
   * long enough for it. Null = nothing to wait for (off shift / no gap).
   */
  freeAt: Date | null;
  /** The busy step's "After …" option ("After Current Appointment"). */
  afterLabel: string;
  /** Today's occupying appointments / blocks, by start. */
  busy: BusyInterval[];
  /** Today's shift end; null = not working today. */
  shiftEnd: Date | null;
  /** Busy line: "With Ben Ortiz until 2:30 PM" / "Break until …" / off. */
  busyText: string;
  /** The row's second line ("Free until 2:30 PM" / busy text). */
  sub: string;
}

/** Fake-local wall clock "2:30 PM" (native getters, never moment). */
export const wallClock = (d: Date): string => {
  const h = d.getHours();
  return `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};
const clock = wallClock;
const WEEKDAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const minutesOfDay = (raw: string | null | undefined): number | null => {
  if (!raw) return null;
  const m = moment(raw, ["h:mm A", "HH:mm:ss", "HH:mm"]);
  return m.isValid() ? m.hours() * 60 + m.minutes() : null;
};

/**
 * Today's working window in minutes (override for the date wins: both
 * times = working, both null = day off; else the weekly schedule).
 */
export const workingWindow = (
  staff: Employee,
  now: Date,
  overrides: readonly StaffHoursOverride[],
): { start: number; end: number } | null => {
  const day = dayOf(now);
  const weekday = WEEKDAYS[now.getDay()];
  const override = overrides.find(
    (o) => o.staff_id === staff.id && o.work_date === day,
  );
  const row = staff as unknown as Record<string, string | undefined>;
  const start = minutesOfDay(
    override ? override.start_time : row[`${weekday}_start_time`],
  );
  const end = minutesOfDay(
    override ? override.end_time : row[`${weekday}_end_time`],
  );
  return start === null || end === null ? null : { start, end };
};

const occupies = (e: CalendarEventType): boolean => {
  if ((e as { isTransient?: boolean }).isTransient) return false;
  const status = (e as { status?: string }).status;
  return !status || !FREE_STATUSES.has(status);
};

const busyLabel = (e: CalendarEventType): string => {
  if (e.isTimeBlock) return (e as { type?: string }).type || "Time block";
  const a = e as { firstName?: string; lastName?: string };
  const who = `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim();
  return who ? `With ${who}` : "With a client";
};

const MINUTE_MS = 60 * 1000;
const AFTER_CURRENT = "After Current Appointment";

/** `now`'s day at `minutes` past midnight (fake-local). */
const atMinutes = (now: Date, minutes: number): Date =>
  new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    Math.floor(minutes / 60),
    minutes % 60,
  );

const initialsOf = (first?: string, last?: string) =>
  `${(first || "")[0] || ""}${(last || "")[0] || ""}`.toUpperCase() || "—";

/**
 * Availability of each provider at `now` (salon wall clock as a fake-local
 * Date, the same space as the calendar events), from the events and hours
 * the calendar already loaded. Available providers first, then the given
 * order.
 */
export function staffAvailability({
  staff,
  events,
  now,
  overrides,
}: {
  staff: readonly Employee[];
  events: readonly CalendarEventType[];
  now: Date;
  overrides: readonly StaffHoursOverride[];
}): StaffAvailability[] {
  const nowMs = now.getTime();
  const dayKey = dayOf(now);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const rows = staff.map((s): StaffAvailability => {
    const mine = events
      .filter(
        (e) =>
          e.resourceId === s.id && occupies(e) && dayOf(e.start) === dayKey,
      )
      .sort((a, b) => a.start.getTime() - b.start.getTime());
    const current = mine
      .filter((e) => e.start.getTime() <= nowMs && e.end.getTime() > nowMs)
      .sort((a, b) => b.end.getTime() - a.end.getTime())[0];
    const next = mine.find((e) => e.start.getTime() > nowMs);
    const window = workingWindow(s, now, overrides);
    const working = !!window && nowMin >= window.start && nowMin < window.end;
    const available = !current && working;
    const busyText = current
      ? `${busyLabel(current)} until ${clock(current.end)}`
      : !window
        ? "Not working today"
        : working
          ? ""
          : "Outside working hours";
    const nextAt = available && next ? next.start : null;
    const name = `${s.first_name} ${s.last_name}`.trim();
    return {
      id: s.id,
      name,
      initials: initialsOf(s.first_name, s.last_name),
      available,
      working,
      nextAt,
      freeAt: current ? current.end : null,
      afterLabel: AFTER_CURRENT,
      busy: mine.map((e) => ({
        start: e.start,
        end: e.end,
        label: busyLabel(e),
      })),
      shiftEnd: window ? atMinutes(now, window.end) : null,
      busyText,
      sub: availableSub(available, nextAt, busyText),
    };
  });
  return availableFirst(rows);
}

const availableSub = (
  available: boolean,
  nextAt: Date | null,
  busyText: string,
): string =>
  available
    ? nextAt
      ? `Free until ${clock(nextAt)}`
      : "Free for the rest of the day"
    : busyText;

const availableFirst = (rows: StaffAvailability[]): StaffAvailability[] => [
  ...rows.filter((r) => r.available),
  ...rows.filter((r) => !r.available),
];

/** Where a service fits: its start and the bookings it waits behind. */
export interface ServiceFit {
  start: Date;
  waited: BusyInterval[];
}

/**
 * The earliest start at or after `now` with `minutes` free before the next
 * appointment / block and the shift end; null = no gap fits today. Walks
 * the day's bookings in start order, pushing the start past each one the
 * service would overlap (so a 45 min cut doesn't "fit" a 20 min gap).
 */
export const earliestFit = (
  busy: readonly BusyInterval[],
  now: Date,
  minutes: number,
  shiftEnd: Date,
): ServiceFit | null => {
  let start = now;
  const waited: BusyInterval[] = [];
  for (const b of busy) {
    if (b.end.getTime() <= start.getTime()) continue;
    if (b.start.getTime() - start.getTime() >= minutes * MINUTE_MS) break;
    waited.push(b);
    start = b.end;
  }
  return start.getTime() + minutes * MINUTE_MS <= shiftEnd.getTime()
    ? { start, waited }
    : null;
};

/** "After Current Appointment" / "After Upcoming Appointment(s)" / both. */
export const afterLabelFor = (waited: readonly BusyInterval[], now: Date) => {
  const current = !!waited[0] && waited[0].start.getTime() <= now.getTime();
  if (waited.length > 1)
    return current
      ? "After Current & Upcoming Appointments"
      : "After Upcoming Appointments";
  return current ? AFTER_CURRENT : "After Upcoming Appointment";
};

/**
 * A row re-judged for a service `minutes` long: available only when it
 * fits from now; otherwise `freeAt` is the first gap that fits (null = none
 * today). Off-shift rows are unchanged ("Start now" only).
 */
export const fitToService = (
  row: StaffAvailability,
  minutes: number,
  now: Date,
): StaffAvailability => {
  if (!row.working || !row.shiftEnd) return row;
  const fit = earliestFit(row.busy, now, minutes, row.shiftEnd);
  const available = !!fit && fit.waited.length === 0;
  if (available) return { ...row, available, freeAt: null };
  const blocking =
    fit?.waited[0] ?? row.busy.find((b) => b.end.getTime() > now.getTime());
  const busyText = !blocking
    ? `Shift ends at ${clock(row.shiftEnd)}`
    : blocking.start.getTime() <= now.getTime()
      ? `${blocking.label} until ${clock(blocking.end)}`
      : `${blocking.label} at ${clock(blocking.start)}`;
  return {
    ...row,
    available,
    nextAt: null,
    freeAt: fit ? fit.start : null,
    afterLabel: fit ? afterLabelFor(fit.waited, now) : AFTER_CURRENT,
    busyText,
    sub: busyText,
  };
};

/** The provider's length for the service (base + processing + finishing). */
export const serviceMinutesFor = (
  service: SalonService | undefined,
  staffId: string,
  fallback: number,
): number => {
  if (!service) return fallback;
  const f = staffServiceFields(service, staffId);
  return f.baseDuration + f.processingTime + f.finishingTime;
};

/**
 * Providers who perform the service (servicing_staff), each fitted to their
 * length for it as of `now` (salon wall clock, fake-local), available first.
 */
export const qualifiedFor = (
  rows: readonly StaffAvailability[],
  service: SalonService | undefined,
  now: Date,
): StaffAvailability[] =>
  service
    ? availableFirst(
        rows
          .filter((r) => !!staffServiceFor(service, r.id))
          .map((r) =>
            fitToService(r, serviceMinutesFor(service, r.id, 30), now),
          ),
      )
    : [];
